import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';
import { QuoteBuilder, type QuoteRow } from '../components/QuoteForms';

type Quote = QuoteRow & {
  quote_number: string | null; version: number; status: string; subtotal: number; total: number;
  accept_token: string; sent_at: string | null; accepted_at: string | null; accepted_by_name: string | null; contract_id: string | null;
  clients: { name: string } | null; properties: { name: string } | null;
};
const TONE: Record<string, 'open' | 'inprogress' | 'done' | 'overdue' | 'onhold'> = { draft: 'open', sent: 'inprogress', accepted: 'done', declined: 'overdue', expired: 'onhold' };

export function Quotes() {
  const { t, i18n } = useTranslation();
  const { timezone } = useOrg();
  const [rows, setRows] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<QuoteRow | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const r = await supabase.from('quotes')
      .select('id,quote_number,title,version,status,currency,subtotal,total,valid_until,terms,client_id,property_id,accept_token,sent_at,accepted_at,accepted_by_name,contract_id,clients(name),properties(name)')
      .is('deleted_at', null).order('created_at', { ascending: false });
    const list = (r.data as unknown as Quote[]) ?? [];
    setRows(list); setSel((cur) => cur ?? list[0]?.id ?? null); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = rows.find((q) => q.id === sel) ?? null;
  const acceptUrl = current ? `${window.location.origin}/q/${current.accept_token}` : '';

  async function send(q: Quote) { await supabase.from('quotes').update({ status: 'sent', sent_at: new Date().toISOString() }).eq('id', q.id); load(); }
  async function newVersion(q: Quote) { const { data } = await supabase.rpc('new_quote_version', { p_quote: q.id }); if (data) { setSel(data as string); load(); } }

  return (
    <Layout header={<Topbar actions={<Button onClick={() => { setEditing(null); setFormOpen(true); }}>+ {t('quote.new')}</Button>} />}>
      <div className="px-7 pt-6"><h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.quotes')}</h1></div>
      <div className="flex flex-wrap items-start gap-[18px] px-7 pb-7 pt-[18px]">
        <Card className="min-w-0 max-w-[460px] flex-1 overflow-hidden p-0" style={{ flexBasis: 380 }}>
          {loading ? <div className="p-6 text-sm text-muted">{t('common.loading')}</div>
            : rows.length === 0 ? <div className="p-6 text-sm text-muted">{t('common.noData')}</div>
            : rows.map((q) => (
              <button key={q.id} onClick={() => setSel(q.id)} className={cn('flex w-full flex-col gap-1.5 border-b border-divider p-4 text-left last:border-0', q.id === sel ? 'bg-[#F5F7FF] shadow-[inset_3px_0_0_var(--pv-accent)]' : 'hover:bg-bg')}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium"><span className="font-mono text-xs text-muted">{q.quote_number}{q.version > 1 ? `·v${q.version}` : ''}</span> {q.title ?? '—'}</span>
                  <span className="font-mono text-xs">{formatCurrency(q.total, q.currency, i18n.language)}</span>
                </div>
                <div className="flex items-center gap-2"><StatusPill tone={TONE[q.status] ?? 'open'}>{t('quote.st_' + q.status)}</StatusPill><span className="text-sm text-muted">{q.clients?.name ?? q.properties?.name ?? ''}</span></div>
              </button>
            ))}
        </Card>

        {current && (
          <Card className="min-w-0 flex-[2] p-6" style={{ flexBasis: 480 }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="font-mono text-xs text-muted">{current.quote_number}{current.version > 1 ? ` · v${current.version}` : ''}</span>
                <h2 className="text-[20px] font-semibold">{current.title ?? '—'}</h2>
                <span className="text-sm text-muted">{[current.clients?.name, current.properties?.name].filter(Boolean).join(' · ')}</span>
              </div>
              <StatusPill tone={TONE[current.status] ?? 'open'}>{t('quote.st_' + current.status)}</StatusPill>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {current.status === 'draft' && <Button size="sm" onClick={() => { setEditing(current); setFormOpen(true); }}>{t('form.edit')}</Button>}
              {current.status === 'draft' && <Button size="sm" variant="accent" onClick={() => send(current)}>{t('quote.send')}</Button>}
              <Button size="sm" variant="secondary" onClick={() => newVersion(current)}>{t('quote.newVersion')}</Button>
              <Link to={`/quotes/${current.id}/print`} target="_blank"><Button size="sm" variant="secondary">{t('quote.print')}</Button></Link>
              {current.contract_id && <span className="rounded-pill bg-st-done-bg px-2.5 py-1 text-xs text-st-done-fg">{t('quote.contractCreated')}</span>}
            </div>

            {['sent', 'accepted', 'expired'].includes(current.status) && (
              <div className="mt-4 flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted">{t('quote.acceptLink')}</span>
                <div className="flex items-center gap-2">
                  <code className="block flex-1 overflow-x-auto rounded-md bg-[#F7F8FA] px-3 py-2 font-mono text-xs">{acceptUrl}</code>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(acceptUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? t('mkt.copied') : t('mkt.copy')}</Button>
                </div>
              </div>
            )}
            {current.accepted_at && <p className="mt-2 text-sm text-st-done-fg">{t('quote.acceptedBy', { name: current.accepted_by_name })} · {fmtDate(current.accepted_at, i18n.language, timezone)}</p>}

            <QuoteLines quoteId={current.id} currency={current.currency} total={current.total} />
          </Card>
        )}
      </div>
      <QuoteBuilder open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={editing} />
    </Layout>
  );
}

function QuoteLines({ quoteId, currency, total }: { quoteId: string; currency: string; total: number }) {
  const { t, i18n } = useTranslation();
  const [lines, setLines] = useState<{ id: string; description: string; quantity: number; unit_price: number; line_total: number }[]>([]);
  useEffect(() => {
    supabase.from('quote_lines').select('id,description,quantity,unit_price,line_total').eq('quote_id', quoteId).is('deleted_at', null).order('position')
      .then((r) => setLines((r.data as any[]) ?? []));
  }, [quoteId]);
  return (
    <div className="mt-5 border-t border-divider pt-4">
      <table className="w-full border-collapse text-sm">
        <tbody>
          {lines.map((l) => (
            <tr key={l.id} className="border-b border-divider last:border-0">
              <td className="py-2.5 pr-2">{l.description}</td>
              <td className="py-2.5 pr-2 text-right font-mono text-xs text-muted">{l.quantity} × {formatCurrency(l.unit_price, currency, i18n.language)}</td>
              <td className="py-2.5 text-right font-mono">{formatCurrency(l.line_total, currency, i18n.language)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex justify-end"><span className="font-mono text-base font-semibold">{t('cost.total')}: {formatCurrency(total, currency, i18n.language)}</span></div>
    </div>
  );
}
